import { ChevronDown } from "lucide-react";
import * as React from "react";
import { Avatar, AvatarFallback } from "../atoms/avatar.js";
import { Badge, type BadgeVariant } from "../atoms/badge.js";
import { Card, CardContent } from "../atoms/card.js";
import { Typography } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";
import { Collapsible, CollapsibleContent } from "../molecules/collapsible.js";
import { DetailsGrid, type DetailsGridRow } from "./details-grid.js";

export type TeamMemberCardTone = "surface" | "tinted";

export interface TeamMemberCardStatus {
  /** Status label shown as a badge beside the name. */
  label: React.ReactNode;
  /** Badge tone. Defaults to neutral. */
  variant?: BadgeVariant;
}

export interface TeamMemberCardSection {
  /** Stable section id. */
  id: string;
  /** Section heading shown above the details grid. */
  title: React.ReactNode;
  /** Read-only detail rows. */
  rows: DetailsGridRow[];
}

export interface TeamMemberCardProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Primary display name. */
  name: React.ReactNode;
  /** Initials shown in the avatar fallback. Ignored when `avatar` is set. */
  initials: string;
  /**
   * Custom leading visual in place of the initials avatar — e.g. a
   * beneficiary-type icon. When provided it replaces the `<Avatar>` entirely;
   * pass an element that reads as a ~2.5rem square (the `md` avatar footprint).
   */
  avatar?: React.ReactNode;
  /** Supporting text below the name, such as role or employment metadata. */
  description?: React.ReactNode;
  /** Optional role badge, e.g. Administrator. */
  roleLabel?: React.ReactNode;
  /** Status badge beside the title. */
  status?: TeamMemberCardStatus;
  /** Menu or icon actions rendered in the header. */
  headerActions?: React.ReactNode;
  /** Detail sections revealed by the expand control. */
  sections?: TeamMemberCardSection[];
  /** Actions rendered below expanded details. */
  footerActions?: React.ReactNode;
  /** Default expanded state when uncontrolled. */
  defaultExpanded?: boolean;
  /** Controlled expanded state. */
  expanded?: boolean;
  /** Called when the expand control changes. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Visual tone. Default surface. */
  tone?: TeamMemberCardTone;
  /** Accessible label for the expand button when collapsed. */
  expandLabel?: string;
  /** Accessible label for the expand button when expanded. */
  collapseLabel?: string;
}

/**
 * Generic member/profile card for people lists. Apps provide domain labels,
 * menu actions, and read-only rows; the organism owns the visual anatomy.
 */
export const TeamMemberCard = React.forwardRef<HTMLElement, TeamMemberCardProps>(
  function TeamMemberCard(
    {
      avatar,
      className,
      collapseLabel = "Hide details",
      defaultExpanded = false,
      description,
      expanded,
      expandLabel = "Show details",
      footerActions,
      headerActions,
      initials,
      name,
      onExpandedChange,
      roleLabel,
      sections = [],
      status,
      tone = "surface",
      ...props
    },
    ref,
  ) {
    const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(defaultExpanded);
    const isExpanded = expanded ?? uncontrolledExpanded;
    const hasDetails = sections.length > 0 || Boolean(footerActions);

    const toggleExpanded = () => {
      const next = !isExpanded;
      setUncontrolledExpanded(next);
      onExpandedChange?.(next);
    };

    return (
      <Card
        ref={ref}
        className={cx(
          "cw-v2-team-member-card",
          "cw-v2-animate-fade-in-up",
          `cw-v2-team-member-card--${tone}`,
          isExpanded ? "cw-v2-team-member-card--expanded" : null,
          className,
        )}
        {...props}
      >
        <CardContent className="cw-v2-team-member-card__content">
          <div className="cw-v2-team-member-card__header">
            <button
              type="button"
              className="cw-v2-team-member-card__summary"
              aria-label={isExpanded ? collapseLabel : expandLabel}
              aria-expanded={hasDetails ? isExpanded : undefined}
              onClick={hasDetails ? toggleExpanded : undefined}
              disabled={!hasDetails}
            >
              {avatar ? (
                <span className="cw-v2-team-member-card__avatar" aria-hidden>
                  {avatar}
                </span>
              ) : (
                <Avatar
                  size="md"
                  className={cx(
                    "cw-v2-team-member-card__avatar",
                    tone === "tinted" ? "cw-v2-team-member-card__avatar--inverse" : null,
                  )}
                >
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
              )}

              <div className="cw-v2-team-member-card__identity">
                <div className="cw-v2-team-member-card__title-row">
                  <Typography
                    variant="heading-small"
                    as="h3"
                    className="cw-v2-team-member-card__name"
                  >
                    {name}
                  </Typography>
                  {roleLabel ? (
                    <Badge size="sm" variant="outline">
                      {roleLabel}
                    </Badge>
                  ) : null}
                  {status ? (
                    <Badge size="sm" variant={status.variant ?? "neutral"}>
                      {status.label}
                    </Badge>
                  ) : null}
                </div>
                {description ? (
                  <Typography
                    variant="body"
                    as="div"
                    className="cw-v2-team-member-card__description"
                  >
                    {description}
                  </Typography>
                ) : null}
              </div>

              {hasDetails ? (
                <ChevronDown
                  aria-hidden
                  className={cx(
                    "cw-v2-team-member-card__chevron",
                    isExpanded ? "cw-v2-team-member-card__chevron--expanded" : null,
                  )}
                />
              ) : null}
            </button>

            {headerActions ? (
              <div className="cw-v2-team-member-card__controls">{headerActions}</div>
            ) : null}
          </div>

          {hasDetails ? (
            <Collapsible open={isExpanded} className="cw-v2-team-member-card__collapsible">
              <CollapsibleContent>
                <div className="cw-v2-team-member-card__details">
                  {sections.map((section) => (
                    <DetailsGrid
                      key={section.id}
                      title={
                        <Typography variant="heading-small" as="h4">
                          {section.title}
                        </Typography>
                      }
                      rows={section.rows}
                      columns={3}
                    />
                  ))}
                  {footerActions ? (
                    <div className="cw-v2-team-member-card__footer-actions">{footerActions}</div>
                  ) : null}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </CardContent>
      </Card>
    );
  },
);
TeamMemberCard.displayName = "TeamMemberCard";
